import { useTranslation } from "react-i18next";
import type { Provider } from "../../../../../../store/reseller-admin/providersStore";
import type { ThemeClasses } from "../types";
import { useIsUserDev } from "../../../../../../hooks/useIsUserDev";

interface ProviderGridCardProps {
  provider: Provider;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onDetails: (provider: Provider) => void;
  onEdit: (provider: Provider) => void;
  onDelete: (provider: Provider) => void;
  editLoading?: boolean;
}

export default function ProviderGridCard({
  provider,
  themeClasses,
  isDarkMode,
  onDetails,
  onEdit,
  onDelete,
  editLoading = false,
}: ProviderGridCardProps) {
  const { t } = useTranslation("resellerAdmin");
  const { isUserDev } = useIsUserDev();

  const adapterLabel = provider.adapter
    ? t(`providers.options.adapter.${provider.adapter}`, {
        defaultValue: provider.adapter,
      })
    : t("providers.noData");
  const credentialsCount = provider.credentials_schema?.length || 0;

  return (
    <div
      className={`rounded-2xl border ${themeClasses.cardBorder} ${themeClasses.cardBg} p-5 flex flex-col gap-3 transition-all hover:shadow-lg`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
              isDarkMode ? "bg-primary-500/15" : "bg-primary-50"
            }`}
          >
            <i className="ri-cloud-line text-lg text-primary-500"></i>
          </div>
          <div className="min-w-0">
            <h3 className={`font-semibold truncate ${themeClasses.text}`}>
              {provider.name}
            </h3>
            <span
              className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full mt-1 ${
                provider.active
                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                  : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
              }`}
            >
              <i
                className={`${
                  provider.active ? "ri-checkbox-circle-fill" : "ri-close-circle-fill"
                }`}
              ></i>
              {provider.active ? t("providers.active") : t("providers.inactive")}
            </span>
          </div>
        </div>
        {provider.supports_discovery && (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400 shrink-0">
            <i className="ri-radar-line mr-1"></i>
            {t("providers.discovery")}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {t("providers.fields.adapter")}
          </p>
          <p className={`${themeClasses.text} truncate`}>{adapterLabel}</p>
        </div>
        <div>
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {t("providers.fields.pollInterval")}
          </p>
          <p className={`${themeClasses.text} truncate`}>
            {provider.default_poll_interval_sec || t("providers.noData")}
          </p>
        </div>
        <div className="col-span-2">
          <p className={`text-xs ${themeClasses.textSecondary}`}>
            {t("providers.fields.endpointBase")}
          </p>
          <p className={`${themeClasses.text} truncate`}>
            {provider.endpoints?.base || t("providers.noData")}
          </p>
        </div>
      </div>

      <p className={`text-xs ${themeClasses.textSecondary}`}>
        <i className="ri-key-2-line mr-1"></i>
        {t("providers.credentialsCount", { count: credentialsCount })}
      </p>

      <div className={`flex items-center gap-2 pt-3 border-t ${themeClasses.border}`}>
        <button
          onClick={() => onDetails(provider)}
          className={`flex-1 text-sm py-2 rounded-lg ${themeClasses.buttonHover} ${themeClasses.buttonText} transition-all`}
        >
          <i className="ri-eye-line mr-1"></i>
          {t("providers.actions.details")}
        </button>
        {isUserDev && (
          <>
            <button
              onClick={() => onEdit(provider)}
              disabled={editLoading}
              className="flex-1 text-sm py-2 rounded-lg bg-primary-500/10 text-primary-600 dark:text-primary-400 hover:bg-primary-500/20 transition-all disabled:opacity-60 disabled:cursor-wait inline-flex items-center justify-center"
            >
              {editLoading ? (
                <i className="ri-loader-4-line animate-spin mr-1"></i>
              ) : (
                <i className="ri-edit-line mr-1"></i>
              )}
              {t("providers.actions.edit")}
            </button>
            <button
              onClick={() => onDelete(provider)}
              className="p-2 rounded-lg text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 transition-all"
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
